import { router } from '@inertiajs/react'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'

export interface TaskWidget {
  id: string
  details: string
  state: string
  pic_names: string[]
  has_reviewers: boolean
}

// Task state → badge, shared with the pricing page's widget.
export const TASK_STATE_BADGE: Record<string, { label: string; variant: 'secondary' | 'info' | 'warning' }> = {
  pending: { label: 'Pending', variant: 'secondary' },
  submitted_for_review: { label: 'In review', variant: 'info' },
  changes_requested: { label: 'Changes requested', variant: 'warning' }
}

// How many of the plan's 3 steps carry a Complete stamp. Pages pass their
// live values (React state after a Complete click), not the initial props,
// so the counter moves without a reload.
export function stepsDone(
  overallCompletedAt: string | null,
  channelCompletedAt: string | null,
  quarterlyCompletedAt: string | null
): number {
  return [overallCompletedAt, channelCompletedAt, quarterlyCompletedAt].filter(Boolean).length
}

// Header widget on the Top Down edit pages: the connected cycle task's state,
// PICs, and step progress, with the Submit verb as a link to the submit page
// (Plan/TopDown/Submit) — enabled once all three steps are complete. While
// the task is in review the slot holds a link to the submission's review
// page instead, so the PIC can reread it and follow the comments. The
// server only sends `task_widget` to the task's PICs (Plan::CycleTaskProps),
// so rendering it at all is already the visibility check.
export default function PlanTaskWidget({ task, done, submitHref, reviewHref }: {
  task: TaskWidget
  done: number
  submitHref: string
  reviewHref: string
}) {
  const badge = TASK_STATE_BADGE[task.state] ?? { label: task.state, variant: 'secondary' as const }
  // In review = the PIC's turn is over; no verb until the reviewer acts.
  const showSubmit = task.state === 'pending' || task.state === 'changes_requested'
  const inReview = task.state === 'submitted_for_review'
  const verb = task.has_reviewers ? 'Submit' : 'Mark completed'

  // In review the whole card opens the submission's review page; the button
  // inside is the visible cue and goes to the same place.
  const open = inReview ? () => router.visit(reviewHref) : undefined
  return (
    <div
      className={`flex min-w-0 flex-col gap-0.5 rounded-lg border px-3 py-1.5${inReview ? ' cursor-pointer transition-colors hover:border-chart-muted' : ''}`}
      style={{ background: 'var(--background)', borderColor: 'var(--border)' }}
      role={inReview ? 'link' : undefined}
      tabIndex={inReview ? 0 : undefined}
      onClick={open}
      onKeyDown={open ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open() } } : undefined}
    >
      <div className="flex items-center gap-2">
        <Badge variant={badge.variant} className="px-1.5 py-0 text-[10px] whitespace-nowrap">
          {badge.label}
        </Badge>
        <span className="max-w-[280px] truncate text-xs font-medium text-foreground" title={task.details}>
          {task.details}
        </span>
      </div>
      <div className="flex items-center gap-2 text-[11px] text-foreground">
        <span className="max-w-[160px] truncate" title={task.pic_names.join(', ')}>
          {task.pic_names.join(', ')}
        </span>
        <span>·</span>
        <span className="whitespace-nowrap">{done}/3 Done</span>
        {showSubmit && (
          <Button
            size="sm"
            className="ml-auto h-5 px-2 text-[11px]"
            disabled={done < 3}
            onClick={() => router.visit(submitHref)}
          >
            {verb}
          </Button>
        )}
        {inReview && (
          <Button
            size="sm"
            variant="outline"
            className="ml-auto h-5 px-2 text-[11px]"
            onClick={(e) => { e.stopPropagation(); router.visit(reviewHref) }}
          >
            View submission
          </Button>
        )}
      </div>
    </div>
  )
}
